function projectRef(url) {
  try {
    return new URL(url).hostname.split('.')[0];
  } catch {
    return '';
  }
}

function storedSession(url, storage) {
  const ref = projectRef(url);
  if (!ref || !storage) return null;
  try {
    const session = JSON.parse(storage.getItem(`sb-${ref}-auth-token`) || 'null');
    return session?.access_token ? session : null;
  } catch {
    return null;
  }
}

function defaultStorage() {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

async function readJson(response) {
  return response.json().catch(() => null);
}

/**
 * Optional, failure-tolerant transport for Dungeon Market's shared bazaar.
 * It deliberately has no npm dependency: Melodia's existing Supabase browser
 * session is enough to authenticate writes, while public boards use the anon
 * key. Every method returns a result object and never throws into the game UI.
 */
export class DungeonMarketCloud {
  constructor({
    url = '',
    anonKey = '',
    fetchImpl = globalThis.fetch,
    storage,
    sessionProvider = null,
    sessionRefresher = null,
    requestTimeoutMs = 12000,
  } = {}) {
    this.url = url.replace(/\/$/, '');
    this.anonKey = anonKey;
    this.fetchImpl = fetchImpl;
    this.storage = storage === undefined ? defaultStorage() : storage;
    this.sessionProvider = typeof sessionProvider === 'function' ? sessionProvider : null;
    this.sessionRefresher = typeof sessionRefresher === 'function' ? sessionRefresher : null;
    this.requestTimeoutMs = Math.max(10, Math.min(60000, Math.round(Number(requestTimeoutMs) || 12000)));
  }

  get configured() {
    return Boolean(this.url && this.anonKey && this.fetchImpl);
  }

  get session() {
    if (this.sessionProvider) {
      try {
        const session = this.sessionProvider();
        return session?.access_token ? session : null;
      } catch {
        return null;
      }
    }
    return storedSession(this.url, this.storage);
  }

  get signedIn() {
    return Boolean(this.session?.access_token);
  }

  get username() {
    const user = this.session?.user;
    return user?.user_metadata?.username || user?.email?.split('@')[0] || null;
  }

  get userId() {
    return this.session?.user?.id || null;
  }

  async request(path, options = {}) {
    if (!this.configured) return { ok: false, offline: true, error: 'Cloud bazaar is not configured.' };
    const {
      requireAuth = false,
      signal: externalSignal,
      ...requestOptions
    } = options;
    let refreshStatus = null;
    if (this.sessionRefresher && this.signedIn) {
      try {
        refreshStatus = await this.sessionRefresher();
      } catch (error) {
        refreshStatus = {
          offline: true,
          error: error?.message || 'Cloud account session refresh failed.',
        };
      }
    }
    if (requireAuth && !this.signedIn) {
      return {
        ok: false,
        authRequired: true,
        offline: Boolean(refreshStatus?.offline),
        retryable: Boolean(refreshStatus?.retryable),
        error: refreshStatus?.error || 'Your cloud session expired. Sign in again to continue.',
      };
    }
    const controller = new AbortController();
    let timedOut = false;
    const forwardAbort = () => controller.abort();
    if (externalSignal) {
      if (externalSignal.aborted) controller.abort();
      else externalSignal.addEventListener('abort', forwardAbort, { once: true });
    }
    const deadline = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, this.requestTimeoutMs);
    try {
      const response = await this.fetchImpl.call(globalThis, `${this.url}/rest/v1/${path}`, {
        ...requestOptions,
        signal: controller.signal,
        headers: {
          apikey: this.anonKey,
          Authorization: `Bearer ${this.session?.access_token || this.anonKey}`,
          'Content-Type': 'application/json',
          ...(requestOptions.headers || {}),
        },
      });
      const data = await readJson(response);
      if (!response.ok) {
        const retryable = response.status === 408
          || response.status === 425
          || response.status === 429
          || response.status >= 500;
        return {
          ok: false,
          offline: response.status === 408 || response.status >= 500,
          retryable,
          status: response.status,
          error: data?.message || data?.hint || `Cloud bazaar request failed (${response.status}).`,
        };
      }
      return { ok: true, data };
    } catch (error) {
      return {
        ok: false,
        offline: true,
        retryable: true,
        timedOut,
        error: timedOut
          ? 'Cloud bazaar request timed out. Check your connection and try again.'
          : error?.message || 'Cloud bazaar is unreachable.',
      };
    } finally {
      clearTimeout(deadline);
      externalSignal?.removeEventListener?.('abort', forwardAbort);
    }
  }

  leaderboard(limit = 50) {
    const safeLimit = Math.max(1, Math.min(100, Math.round(Number(limit) || 50)));
    return this.request(`dm_leaderboard?select=display_name,weekly_score,lifetime_score,discoveries,updated_at,is_you&order=weekly_score.desc&limit=${safeLimit}`);
  }

  market(limit = 30) {
    const safeLimit = Math.max(1, Math.min(100, Math.round(Number(limit) || 30)));
    return this.request(`dm_market_board?select=id,seller_name,item_id,quantity,price,created_at&order=created_at.desc&limit=${safeLimit}`);
  }

  myListings(limit = 30) {
    if (!this.signedIn) return Promise.resolve({ ok: false, authRequired: true, error: 'Sign in to manage your listings.' });
    const safeLimit = Math.max(1, Math.min(100, Math.round(Number(limit) || 30)));
    return this.request('rpc/dm_my_listings', {
      requireAuth: true,
      method: 'POST',
      headers: { Prefer: 'return=representation' },
      body: JSON.stringify({ p_limit: safeLimit }),
    });
  }

  tradeHistory(limit = 20) {
    if (!this.signedIn) return Promise.resolve({ ok: false, authRequired: true, error: 'Sign in to review your trade history.' });
    const safeLimit = Math.max(1, Math.min(50, Math.round(Number(limit) || 20)));
    return this.request('rpc/dm_trade_history', {
      requireAuth: true,
      method: 'POST',
      headers: { Prefer: 'return=representation' },
      body: JSON.stringify({ p_limit: safeLimit }),
    });
  }

  bootstrap() {
    if (!this.signedIn) return Promise.resolve({ ok: false, authRequired: true, error: 'Sign in to enter the online bazaar.' });
    return this.request('rpc/dm_bootstrap_player', {
      requireAuth: true,
      method: 'POST',
      headers: { Prefer: 'return=representation' },
      body: '{}',
    });
  }

  syncProgress(eventId, delta, discoveries) {
    if (!this.signedIn) return Promise.resolve({ ok: false, authRequired: true, error: 'Sign in to sync your merchant ledger.' });
    return this.request('rpc/dm_sync_progress', {
      requireAuth: true,
      method: 'POST',
      headers: { Prefer: 'return=representation' },
      body: JSON.stringify({
        p_event_id: eventId,
        p_gold_delta: Math.trunc(Number(delta?.gold) || 0),
        p_gems_delta: Math.trunc(Number(delta?.gems) || 0),
        p_market_score_delta: Math.trunc(Number(delta?.marketScore) || 0),
        p_discoveries: Math.max(0, Math.trunc(Number(discoveries) || 0)),
        p_inventory_delta: delta?.inventory || {},
      }),
    });
  }

  buyListing(listingId) {
    if (!this.signedIn) return Promise.resolve({ ok: false, authRequired: true, error: 'Sign in to trade with other players.' });
    return this.request('rpc/dm_buy_listing', {
      requireAuth: true,
      method: 'POST',
      headers: { Prefer: 'return=representation' },
      body: JSON.stringify({ p_listing_id: listingId }),
    });
  }

  createListing(itemId, quantity, price) {
    if (!this.signedIn) return Promise.resolve({ ok: false, authRequired: true, error: 'Sign in to list items online.' });
    return this.request('rpc/dm_create_listing', {
      requireAuth: true,
      method: 'POST',
      headers: { Prefer: 'return=representation' },
      body: JSON.stringify({
        p_item_id: itemId,
        p_quantity: Math.max(1, Math.min(999, Math.round(Number(quantity) || 1))),
        p_price: Math.max(1, Math.round(Number(price) || 1)),
      }),
    });
  }

  cancelListing(listingId) {
    if (!this.signedIn) return Promise.resolve({ ok: false, authRequired: true, error: 'Sign in to cancel a listing.' });
    return this.request('rpc/dm_cancel_listing', {
      requireAuth: true,
      method: 'POST',
      headers: { Prefer: 'return=representation' },
      body: JSON.stringify({ p_listing_id: listingId }),
    });
  }

  socialRoster(limit = 30) {
    if (!this.signedIn) return Promise.resolve({ ok: false, authRequired: true, error: 'Sign in to meet other merchants.' });
    const safeLimit = Math.max(1, Math.min(100, Math.round(Number(limit) || 30)));
    return this.request('rpc/dm_social_roster', {
      requireAuth: true,
      method: 'POST',
      headers: { Prefer: 'return=representation' },
      body: JSON.stringify({ p_limit: safeLimit }),
    });
  }

  sendLantern(socialId) {
    if (!this.signedIn) return Promise.resolve({ ok: false, authRequired: true, error: 'Sign in to send a guild lantern.' });
    return this.request('rpc/dm_send_lantern', {
      requireAuth: true,
      method: 'POST',
      headers: { Prefer: 'return=representation' },
      body: JSON.stringify({ p_social_id: socialId }),
    });
  }

  campaignStatus() {
    if (!this.signedIn) return Promise.resolve({ ok: false, authRequired: true, error: 'Sign in to join the guild campaign.' });
    return this.request('rpc/dm_campaign_status', {
      requireAuth: true,
      method: 'POST',
      headers: { Prefer: 'return=representation' },
      body: '{}',
    });
  }

  contributeCampaign() {
    if (!this.signedIn) return Promise.resolve({ ok: false, authRequired: true, error: 'Sign in to dispatch a guild caravan.' });
    return this.request('rpc/dm_contribute_campaign', {
      requireAuth: true,
      method: 'POST',
      headers: { Prefer: 'return=representation' },
      body: '{}',
    });
  }

  claimCampaignTier(tier) {
    if (!this.signedIn) return Promise.resolve({ ok: false, authRequired: true, error: 'Sign in to claim a guild campaign reward.' });
    return this.request('rpc/dm_claim_campaign_tier', {
      requireAuth: true,
      method: 'POST',
      headers: { Prefer: 'return=representation' },
      body: JSON.stringify({ p_tier: Math.round(Number(tier) || 0) }),
    });
  }
}

export function dungeonMarketCloud({ sessionProvider = null, sessionRefresher = null } = {}) {
  return new DungeonMarketCloud({
    url: import.meta.env.VITE_SUPABASE_URL,
    anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    sessionProvider,
    sessionRefresher,
  });
}
